import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Pagination, Spin, Tooltip, Typography } from 'antd';
import { DownOutlined, RadarChartOutlined, RightOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';

const { Text } = Typography;

const TEXT_PRIMARY = '#1f1f1f';
const TEXT_MUTED = '#595959';
const PAGE_SIZE = 12;
const TREE_ARROW_WIDTH = 14;
const TREE_CHECKBOX_SLOT = 22;
const TREE_MONITOR_SLOT = 22;
const TREE_DEPTH_INDENT = 20;
const TREE_COLUMN_GAP = 8;

const panelStyle = {
  flex: 1,
  minWidth: 280,
  border: '1px solid rgba(148, 22, 127, 0.25)',
  borderRadius: 8,
  background: '#faf8fb',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 360,
  maxHeight: 520,
};

const panelBodyStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 10px 4px',
};

const filterBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '10px 10px 8px',
  borderBottom: '1px solid rgba(148, 22, 127, 0.1)',
  background: '#faf8fb',
};

const loadingPanelStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 280,
  padding: '40px 24px',
  border: '1px solid rgba(148, 22, 127, 0.2)',
  borderRadius: 8,
  background: 'linear-gradient(180deg, #ffffff 0%, #faf8fb 100%)',
  textAlign: 'center',
};

const sortTreeNodes = (nodes) => {
  nodes.sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
  nodes.forEach((node) => sortTreeNodes(node.children));
};

const buildPageTree = (pages) => {
  const byId = new Map(
    pages.map((page) => [page.id, { ...page, children: [] }]),
  );
  const roots = [];
  pages.forEach((page) => {
    const node = byId.get(page.id);
    const parentId = page.parent_id;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  sortTreeNodes(roots);
  return roots;
};

const filterTree = (nodes, predicate) => (
  nodes.reduce((acc, node) => {
    const filteredChildren = filterTree(node.children, predicate);
    if (predicate(node) || filteredChildren.length) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, [])
);

const flattenTree = (nodes) => {
  const rows = [];
  const walk = (list) => {
    list.forEach((node) => {
      rows.push(node);
      walk(node.children);
    });
  };
  walk(nodes);
  return rows;
};

const collectDescendantIds = (id, childrenByParent) => {
  const result = [];
  const queue = [...(childrenByParent.get(id) || [])];
  while (queue.length) {
    const next = queue.shift();
    result.push(next);
    queue.push(...(childrenByParent.get(next) || []));
  }
  return result;
};

const isFullySelected = (id, selectedSet, childrenByParent) => {
  if (!selectedSet.has(id)) return false;
  return collectDescendantIds(id, childrenByParent).every((descId) => selectedSet.has(descId));
};

/** Map an explicit selected-id set to wiki paths without re-including excluded descendants. */
const compressSelectedSetToPaths = (selectedSet, pagesById, childrenByParent) => {
  const paths = [];
  const covered = new Set();

  const emitFrom = (id) => {
    if (!selectedSet.has(id) || covered.has(id)) return;
    const page = pagesById.get(id);
    if (!page?.path) return;

    const childIds = childrenByParent.get(id) || [];
    if (!childIds.length || isFullySelected(id, selectedSet, childrenByParent)) {
      paths.push(page.path);
      covered.add(id);
      collectDescendantIds(id, childrenByParent).forEach((descId) => covered.add(descId));
      return;
    }

    childIds.forEach((childId) => emitFrom(childId));

    // Selected folder/page with no selected children — include the page itself.
    if (selectedSet.has(id) && !childIds.some((childId) => selectedSet.has(childId))) {
      paths.push(page.path);
      covered.add(id);
    }
  };

  selectedSet.forEach((id) => {
    const page = pagesById.get(id);
    if (!page) return;
    if (page.parent_id && selectedSet.has(page.parent_id)) return;
    emitFrom(id);
  });

  return paths;
};

/**
 * Azure Wiki page tree picker. Mirrors ConfluencePageScopePicker's dual-pane
 * checkbox-tree UX, but operates on `pages` supplied directly by the parent
 * (no internal fetch) and communicates selection as wiki page paths.
 */
const AzureWikiPageScopePicker = ({
  pages = [],
  loading = false,
  selectedPaths = [],
  onChange,
  onProceed,
  disabled = false,
  typeFilterEnabled = false,
  monitoredPageIds = null,
  onToggleMonitor = null,
  onMonitorAllSelected = null,
  onCheckMonitoredUpdates = null,
  monitorCheckLoading = false,
  monitorAllLoading = false,
  monitoredPageCount = 0,
  selectedPanelFooter = null,
}) => {
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [browsePage, setBrowsePage] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const [collapsedBrowse, setCollapsedBrowse] = useState(() => new Set());
  const [collapsedSelected, setCollapsedSelected] = useState(() => new Set());

  const normalizedPages = useMemo(() => (
    (pages || [])
      .map((page) => ({
        id: String(page.id || page.path || '').trim(),
        path: String(page.path || '').trim(),
        title: String(page.title || '').trim() || _titleFromPath(page.path),
        parent_id: page.parent_id ? String(page.parent_id) : null,
        type: String(page.type || 'page').toLowerCase(),
      }))
      .filter((page) => page.id && page.path)
  ), [pages]);

  const pagesById = useMemo(() => {
    const map = new Map();
    normalizedPages.forEach((page) => map.set(page.id, page));
    return map;
  }, [normalizedPages]);

  const pagesByPath = useMemo(() => {
    const map = new Map();
    normalizedPages.forEach((page) => map.set(page.path, page));
    return map;
  }, [normalizedPages]);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    normalizedPages.forEach((page) => {
      if (!page.parent_id) return;
      if (!map.has(page.parent_id)) map.set(page.parent_id, []);
      map.get(page.parent_id).push(page.id);
    });
    return map;
  }, [normalizedPages]);

  const selectedIds = useMemo(() => {
    const next = new Set();
    (selectedPaths || []).forEach((rawPath) => {
      const path = String(rawPath || '').trim();
      const page = pagesByPath.get(path);
      if (!page) return;
      next.add(page.id);
      collectDescendantIds(page.id, childrenByParent).forEach((id) => next.add(id));
    });
    return next;
  }, [selectedPaths, pagesByPath, childrenByParent]);

  const fullTree = useMemo(() => buildPageTree(normalizedPages), [normalizedPages]);

  const browseTree = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return filterTree(fullTree, (node) => {
      if (typeFilterEnabled && typeFilter !== '__all__' && node.type !== typeFilter) {
        return false;
      }
      if (!q) return true;
      return String(node.title || '').toLowerCase().includes(q)
        || String(node.path || '').toLowerCase().includes(q);
    });
  }, [fullTree, searchText, typeFilter, typeFilterEnabled]);

  const browseFlat = useMemo(() => flattenTree(browseTree), [browseTree]);

  const selectedTree = useMemo(() => (
    filterTree(fullTree, (node) => selectedIds.has(node.id))
  ), [fullTree, selectedIds]);

  const hasSelectedAncestor = useMemo(() => {
    const cache = new Map();
    const check = (id) => {
      if (cache.has(id)) return cache.get(id);
      const page = pagesById.get(id);
      if (!page?.parent_id) {
        cache.set(id, false);
        return false;
      }
      if (selectedIds.has(page.parent_id)) {
        cache.set(id, true);
        return true;
      }
      const viaParent = check(page.parent_id);
      cache.set(id, viaParent);
      return viaParent;
    };
    return check;
  }, [pagesById, selectedIds]);

  const hasMonitoredAncestor = useMemo(() => {
    const cache = new Map();
    const check = (id) => {
      if (cache.has(id)) return cache.get(id);
      const page = pagesById.get(id);
      if (!page?.parent_id) {
        cache.set(id, false);
        return false;
      }
      if (monitoredPageIds?.has?.(page.parent_id)) {
        cache.set(id, true);
        return true;
      }
      const viaParent = check(page.parent_id);
      cache.set(id, viaParent);
      return viaParent;
    };
    return check;
  }, [pagesById, monitoredPageIds]);

  const emitPaths = (nextSelectedSet) => {
    const roots = compressSelectedSetToPaths(nextSelectedSet, pagesById, childrenByParent);
    onChange?.(roots);
  };

  const togglePageId = (id, checked) => {
    if (disabled || loading) return;
    const next = new Set(selectedIds);
    const descendants = collectDescendantIds(id, childrenByParent);
    const blocked = new Set([id, ...descendants]);

    if (checked) {
      next.add(id);
      descendants.forEach((childId) => next.add(childId));
    } else if (hasSelectedAncestor(id)) {
      // Break parent selection so this page can be excluded while siblings stay selected.
      const selectedAncestors = [];
      let current = pagesById.get(id);
      while (current?.parent_id) {
        if (selectedIds.has(current.parent_id)) {
          selectedAncestors.push(current.parent_id);
        }
        current = pagesById.get(current.parent_id);
      }
      selectedAncestors.forEach((ancestorId) => next.delete(ancestorId));
      blocked.forEach((blockedId) => next.delete(blockedId));

      const topAncestorId = selectedAncestors[selectedAncestors.length - 1];
      if (topAncestorId) {
        collectDescendantIds(topAncestorId, childrenByParent).forEach((descendantId) => {
          if (blocked.has(descendantId)) {
            next.delete(descendantId);
            return;
          }
          if (selectedIds.has(descendantId)) {
            next.add(descendantId);
          }
        });
      }
    } else {
      next.delete(id);
      descendants.forEach((childId) => next.delete(childId));
    }
    emitPaths(next);
  };

  const collectMonitorTargets = (id) => {
    const targets = [id, ...collectDescendantIds(id, childrenByParent)];
    const seen = new Set();
    return targets
      .filter((targetId) => selectedIds.has(targetId))
      .filter((targetId) => {
        if (seen.has(targetId)) return false;
        seen.add(targetId);
        return true;
      })
      .map((targetId) => pagesById.get(targetId))
      .filter(Boolean);
  };

  const collectMonitoredAncestors = (id) => {
    const ancestors = [];
    let current = pagesById.get(id);
    while (current?.parent_id) {
      if (monitoredPageIds?.has?.(current.parent_id)) {
        ancestors.push(current.parent_id);
      }
      current = pagesById.get(current.parent_id);
    }
    return ancestors;
  };

  const toggleMonitorId = (id, enabled, node) => {
    if (disabled || loading || monitorAllLoading || !onToggleMonitor) return;
    const subtree = collectMonitorTargets(id);
    if (!subtree.length) return;

    if (enabled) {
      const [rootNode, ...descendantNodes] = subtree;
      onToggleMonitor(rootNode.id, true, rootNode || node, descendantNodes);
      return;
    }

    // Turning monitoring off for one page under a monitored parent must break the parent
    // out, otherwise inheritance immediately re-activates the child.
    const monitoredAncestors = collectMonitoredAncestors(id);
    if (!monitoredAncestors.length) {
      const [rootNode, ...descendantNodes] = subtree;
      onToggleMonitor(rootNode.id, false, rootNode || node, descendantNodes);
      return;
    }

    const blocked = new Set(subtree.map((page) => page.id));
    const disableNodes = [...subtree];
    monitoredAncestors.forEach((ancestorId) => {
      const ancestorPage = pagesById.get(ancestorId);
      if (ancestorPage) disableNodes.push(ancestorPage);
    });

    const enableNodes = [];
    const seenEnable = new Set();
    const topAncestorId = monitoredAncestors[monitoredAncestors.length - 1];
    collectDescendantIds(topAncestorId, childrenByParent).forEach((candidateId) => {
      if (blocked.has(candidateId) || !selectedIds.has(candidateId)) return;
      if (seenEnable.has(candidateId)) return;
      seenEnable.add(candidateId);
      const page = pagesById.get(candidateId);
      if (page) enableNodes.push(page);
    });

    const [rootNode, ...descendantNodes] = disableNodes;
    onToggleMonitor(
      rootNode.id,
      false,
      rootNode || node,
      descendantNodes,
      { remonitorNodes: enableNodes },
    );
  };

  const allSelectedMonitored = useMemo(() => {
    if (!selectedIds.size || !onMonitorAllSelected) return false;
    for (const id of selectedIds) {
      if (!monitoredPageIds?.has?.(id) && !hasMonitoredAncestor(id)) return false;
    }
    return true;
  }, [selectedIds, monitoredPageIds, onMonitorAllSelected, hasMonitoredAncestor]);

  const monitorAllSelected = () => {
    if (disabled || loading || monitorAllLoading || !onMonitorAllSelected || !selectedTree.length) return;
    const enabled = !allSelectedMonitored;
    const batches = selectedTree
      .map((root) => {
        const targets = collectMonitorTargets(root.id);
        if (!targets.length) return null;
        const [rootNode, ...descendantNodes] = targets;
        return { rootNode, descendantNodes };
      })
      .filter(Boolean);
    if (!batches.length) return;
    onMonitorAllSelected(enabled, batches);
  };

  const toggleAllVisible = (checked) => {
    if (disabled || loading) return;
    const next = new Set(selectedIds);
    browseTree.forEach((root) => {
      if (hasSelectedAncestor(root.id)) return;
      const descendants = collectDescendantIds(root.id, childrenByParent);
      if (checked) {
        next.add(root.id);
        descendants.forEach((id) => next.add(id));
      } else {
        next.delete(root.id);
        descendants.forEach((id) => next.delete(id));
      }
    });
    emitPaths(next);
  };

  const toggleCollapsed = (scope, id) => {
    const setter = scope === 'browse' ? setCollapsedBrowse : setCollapsedSelected;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    setBrowsePage(1);
  }, [searchText, typeFilter]);

  const renderPageLabel = (node, { muted = false } = {}) => (
    <span
      style={{
        fontSize: 12,
        lineHeight: 1.5,
        color: muted ? TEXT_MUTED : TEXT_PRIMARY,
        display: 'block',
        paddingTop: 2,
      }}
    >
      <span style={{ fontWeight: muted ? 500 : 600 }}>{node.title}</span>
      {node.path ? (
        <Text style={{ marginLeft: 6, fontSize: 11, color: TEXT_MUTED }}>({node.path})</Text>
      ) : null}
    </span>
  );

  const renderMonitorSlot = (node) => {
    if (!onToggleMonitor) return null;
    const ancestorMonitored = hasMonitoredAncestor(node.id);
    const selfMonitored = monitoredPageIds?.has?.(node.id);
    const active = selfMonitored || ancestorMonitored;
    const tooltip = active
      ? 'Stop monitoring for Azure Wiki updates'
      : 'Monitor for Azure Wiki content updates (includes sub-pages)';
    return (
      <Tooltip title={tooltip}>
        <span
          role="button"
          tabIndex={disabled || loading || monitorAllLoading ? -1 : 0}
          aria-label={active ? `Stop monitoring ${node.title}` : `Monitor ${node.title} for updates`}
          aria-pressed={active}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled || loading || monitorAllLoading) return;
            toggleMonitorId(node.id, !active, node);
          }}
          onKeyDown={(e) => {
            if (disabled || loading || monitorAllLoading) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              toggleMonitorId(node.id, !active, node);
            }
          }}
          style={{
            width: TREE_MONITOR_SLOT,
            height: 16,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled || loading || monitorAllLoading ? 'not-allowed' : 'pointer',
            color: active ? '#1677ff' : TEXT_MUTED,
            background: active ? 'rgba(22, 119, 255, 0.14)' : 'transparent',
            border: active ? '1px solid rgba(22, 119, 255, 0.45)' : '1px solid transparent',
            borderRadius: 4,
            opacity: disabled || loading || monitorAllLoading ? 0.45 : 1,
            lineHeight: 1,
          }}
        >
          <RadarChartOutlined style={{ fontSize: 13, fontWeight: active ? 700 : 400 }} />
        </span>
      </Tooltip>
    );
  };

  const renderTreeNode = (node, { scope, depth = 0 }) => {
    const hasChildren = node.children.length > 0;
    const collapsedSet = scope === 'browse' ? collapsedBrowse : collapsedSelected;
    const expanded = hasChildren && !collapsedSet.has(node.id);
    const ancestorSelected = hasSelectedAncestor(node.id);
    const isSelected = selectedIds.has(node.id);
    const showMonitor = scope === 'selected' && onToggleMonitor;

    return (
      <div key={node.id}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: showMonitor
              ? `${TREE_ARROW_WIDTH}px ${TREE_CHECKBOX_SLOT}px ${TREE_MONITOR_SLOT}px minmax(0, 1fr)`
              : `${TREE_ARROW_WIDTH}px ${TREE_CHECKBOX_SLOT}px minmax(0, 1fr)`,
            columnGap: TREE_COLUMN_GAP,
            alignItems: 'start',
            marginBottom: 4,
            paddingLeft: depth * TREE_DEPTH_INDENT,
          }}
        >
          <span
            style={{
              width: TREE_ARROW_WIDTH,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 5,
            }}
          >
            {hasChildren ? (
              <span
                role="button"
                tabIndex={0}
                onClick={() => toggleCollapsed(scope, node.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCollapsed(scope, node.id);
                  }
                }}
                style={{ cursor: 'pointer', color: '#94167f', lineHeight: 1 }}
              >
                {expanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
              </span>
            ) : null}
          </span>
          <span
            style={{
              width: TREE_CHECKBOX_SLOT,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            <Checkbox
              checked={isSelected}
              disabled={disabled || loading}
              onChange={(e) => togglePageId(node.id, e.target.checked)}
              style={{ margin: 0, padding: 0, lineHeight: 1 }}
            />
          </span>
          {showMonitor ? (
            <span
              style={{
                width: TREE_MONITOR_SLOT,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 2,
              }}
            >
              {renderMonitorSlot(node)}
            </span>
          ) : null}
          <span style={{ minWidth: 0, paddingTop: 1 }}>
            {renderPageLabel(node, { muted: ancestorSelected })}
          </span>
        </div>
        {hasChildren && expanded
          ? node.children.map((child) => renderTreeNode(child, { scope, depth: depth + 1 }))
          : null}
      </div>
    );
  };

  const browseSelectionState = useMemo(() => {
    const selectableRoots = browseTree.filter((root) => !hasSelectedAncestor(root.id));
    if (!selectableRoots.length) return { checked: false, indeterminate: false };
    const fullySelected = selectableRoots.filter((root) => selectedIds.has(root.id)).length;
    return {
      checked: fullySelected === selectableRoots.length,
      indeterminate: fullySelected > 0 && fullySelected < selectableRoots.length,
    };
  }, [browseTree, hasSelectedAncestor, selectedIds]);

  const paginateRoots = (roots, page) => {
    const start = (page - 1) * PAGE_SIZE;
    return roots.slice(start, start + PAGE_SIZE);
  };

  const browsePageRoots = paginateRoots(browseTree, browsePage);
  const selectedPageRoots = paginateRoots(selectedTree, selectedPage);

  const renderPagination = (total, page, onPageChange) => (
    <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(148, 22, 127, 0.1)', background: '#faf8fb' }}>
      <Pagination
        size="small"
        current={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={onPageChange}
        showSizeChanger={false}
        showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} roots`}
        disabled={disabled || loading}
      />
    </div>
  );

  if (loading) {
    return (
      <div style={loadingPanelStyle}>
        <Spin size="large" />
        <Text style={{ display: 'block', marginTop: 16, fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY }}>
          Loading Azure Wiki pages
        </Text>
        <Text style={{ display: 'block', marginTop: 6, fontSize: 13, color: TEXT_MUTED, maxWidth: 360, lineHeight: 1.55 }}>
          Fetching the wiki page tree…
        </Text>
      </div>
    );
  }

  const footerContent = selectedPanelFooter || (onProceed ? (
    <Button
      type="primary"
      disabled={disabled || !(selectedPaths?.length > 0)}
      onClick={() => onProceed()}
    >
      Proceed
    </Button>
  ) : null);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={panelStyle}>
          <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
            Browse pages ({normalizedPages.length})
          </div>
          <Text style={{ display: 'block', padding: '2px 10px 6px', fontSize: 11, color: TEXT_MUTED }}>
            Nested like the Azure Wiki sidebar. Select a page to include its sub-pages automatically.
          </Text>
          <div style={filterBarStyle}>
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />}
              placeholder="Search titles or paths…"
              value={searchText}
              disabled={disabled}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: '1 1 200px' }}
            />
          </div>
          <div style={{ padding: '0 10px 6px' }}>
            <Checkbox
              checked={browseSelectionState.checked}
              indeterminate={browseSelectionState.indeterminate}
              disabled={disabled || !browseTree.length}
              onChange={(e) => toggleAllVisible(e.target.checked)}
              style={{ color: TEXT_PRIMARY, fontSize: 12 }}
            >
              Select all visible ({browseFlat.length})
            </Checkbox>
          </div>
          <div style={panelBodyStyle}>
            {browsePageRoots.length ? (
              browsePageRoots.map((node) => renderTreeNode(node, { scope: 'browse' }))
            ) : (
              <div style={{ padding: '8px 4px' }}>
                <Text style={{ display: 'block', color: TEXT_PRIMARY, fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                  {normalizedPages.length === 0 ? 'No pages found' : 'No matching pages'}
                </Text>
                <Text style={{ color: TEXT_MUTED, fontSize: 12, lineHeight: 1.55 }}>
                  {normalizedPages.length === 0
                    ? 'This wiki has no pages to sync.'
                    : 'Try a different search.'}
                </Text>
              </div>
            )}
          </div>
          {browseTree.length > PAGE_SIZE
            ? renderPagination(browseTree.length, browsePage, setBrowsePage)
            : null}
        </div>

        <div style={panelStyle}>
          <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
            Selected pages ({selectedIds.size || 0})
          </div>
          <Text style={{ display: 'block', padding: '4px 10px 8px', fontSize: 11, color: TEXT_MUTED }}>
            Nested by parent-child hierarchy. Click the arrow to expand or collapse.
            {onToggleMonitor ? ' Use the monitor icon to watch for Azure Wiki content changes.' : ''}
          </Text>
          {onMonitorAllSelected ? (
            <div style={{ padding: '0 10px 8px' }}>
              <Button
                size="small"
                icon={<RadarChartOutlined />}
                loading={monitorAllLoading}
                disabled={disabled || loading || !selectedTree.length}
                onClick={monitorAllSelected}
              >
                {allSelectedMonitored
                  ? `Stop monitoring all (${selectedIds.size})`
                  : `Monitor all (${selectedIds.size})`}
              </Button>
            </div>
          ) : null}
          <div style={panelBodyStyle}>
            {selectedPageRoots.length ? (
              selectedPageRoots.map((node) => renderTreeNode(node, { scope: 'selected' }))
            ) : (
              <Text style={{ color: TEXT_MUTED, fontSize: 12 }}>No pages selected yet</Text>
            )}
          </div>
          {selectedTree.length > PAGE_SIZE
            ? renderPagination(selectedTree.length, selectedPage, setSelectedPage)
            : null}
          {(footerContent || onCheckMonitoredUpdates) ? (
            <div
              style={{
                padding: '8px 10px',
                borderTop: '1px solid rgba(148, 22, 127, 0.1)',
                background: '#faf8fb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {onCheckMonitoredUpdates ? (
                <Tooltip
                  title={
                    monitoredPageCount > 0
                      ? 'Compare monitored pages against Azure Wiki now'
                      : 'Enable monitoring on at least one page first'
                  }
                >
                  <Button
                    size="small"
                    icon={<SyncOutlined />}
                    loading={monitorCheckLoading}
                    disabled={disabled || monitoredPageCount < 1}
                    onClick={() => onCheckMonitoredUpdates()}
                  >
                    Check updates
                  </Button>
                </Tooltip>
              ) : <span />}
              {footerContent}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

function _titleFromPath(path) {
  const parts = String(path || '').split('/').filter(Boolean);
  return parts[parts.length - 1] || 'Untitled';
}

export default AzureWikiPageScopePicker;
